// src/components/profile/CostingProfile.tsx
//
// Profile → Pricing & Costing, reoriented onto the CANONICAL Costing-Studio tables
// (company_labor_categories / company_indirect_rates / company_odc_categories) that
// proposal generation actually consumes — replacing the legacy, $NaN-prone
// enhanced_pricing_data editor. Embeds the proven Costing-Studio page components as
// tabs so users edit, in one place, exactly the data that prices their proposals.

import React, { useEffect, useState } from 'react';
import {
  DollarSign, Users, Calculator, Package, BarChart3, Loader2, CheckCircle, AlertCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  LaborCategoriesPage, IndirectRatesPage, ODCCategoriesPage, WrapRateCalculator,
} from '../workspace/costing';

type Tab = 'labor' | 'indirect' | 'odc' | 'wrap';

interface CostingStats {
  labor: number;
  indirectConfigured: boolean;
  odc: number;
  wrapRate: number | null;
  avgBase: number | null;
}

export function CostingProfile() {
  const { profile } = useAuth();
  const [tab, setTab] = useState<Tab>('labor');
  const [stats, setStats] = useState<CostingStats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    if (!profile?.id) { setLoading(false); return; }
    try {
      const [labor, indirect, odc] = await Promise.all([
        supabase.from('company_labor_categories')
          .select('base_hourly_rate').eq('profile_id', profile.id).eq('is_active', true),
        supabase.from('company_indirect_rates')
          .select('fringe_rate, overhead_rate, ga_rate, profit_rate').eq('profile_id', profile.id).maybeSingle(),
        supabase.from('company_odc_categories')
          .select('id', { count: 'exact', head: true }).eq('profile_id', profile.id).eq('is_active', true),
      ]);

      const ir = indirect.data as any;
      const wrapRate = ir
        ? (1 + (Number(ir.fringe_rate) || 0) / 100) * (1 + (Number(ir.overhead_rate) || 0) / 100) *
          (1 + (Number(ir.ga_rate) || 0) / 100) * (1 + (Number(ir.profit_rate) || 0) / 100)
        : null;
      const rates = (labor.data || []).map((r: any) => Number(r.base_hourly_rate) || 0).filter((n: number) => n > 0);
      const avgBase = rates.length ? rates.reduce((a: number, b: number) => a + b, 0) / rates.length : null;

      setStats({
        labor: (labor.data || []).length,
        indirectConfigured: !!ir,
        odc: odc.count || 0,
        wrapRate,
        avgBase,
      });
    } catch (e) {
      console.warn('CostingProfile stats load failed', e);
    } finally {
      setLoading(false);
    }
  };

  // Refresh stats on mount and whenever the user switches tabs (cheap way to pick up
  // edits made in a child page without threading callbacks through each one).
  useEffect(() => { loadStats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [profile?.id, tab]);

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'labor', label: 'Labor Categories', icon: Users },
    { id: 'indirect', label: 'Indirect Rates', icon: Calculator },
    { id: 'odc', label: 'Other Direct Costs', icon: Package },
    { id: 'wrap', label: 'Wrap Rate', icon: BarChart3 },
  ];

  return (
    <div className="space-y-6">
      {/* Summary banner — sourced from the canonical Costing-Studio tables */}
      <div className="bg-bw-sidebar rounded-xl p-6 text-white">
        <div className="flex items-center mb-1">
          <DollarSign className="h-5 w-5 mr-2" />
          <h2 className="text-xl font-bold">Costing Studio</h2>
        </div>
        <p className="text-blue-100 text-sm max-w-2xl">
          Define the labor rates, indirect wrap, and other direct costs that price every proposal.
          This is the single source of truth your proposals are built from.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-2xl font-bold">{loading ? '—' : stats?.labor ?? 0}</div>
            <div className="text-xs text-blue-100">Labor Categories</div>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-2xl font-bold flex items-center h-8">
              {loading ? '—' : stats?.indirectConfigured
                ? <CheckCircle className="h-6 w-6 text-green-300" />
                : <AlertCircle className="h-6 w-6 text-yellow-300" />}
            </div>
            <div className="text-xs text-blue-100">Indirect Rates</div>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-2xl font-bold">{stats?.wrapRate ? `${stats.wrapRate.toFixed(2)}x` : '—'}</div>
            <div className="text-xs text-blue-100">Wrap Rate</div>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-2xl font-bold">{loading ? '—' : stats?.odc ?? 0}</div>
            <div className="text-xs text-blue-100">Other Direct Costs</div>
          </div>
        </div>
        {stats?.avgBase != null && stats.wrapRate != null && (
          <p className="text-xs text-blue-100 mt-3">
            Avg base ${stats.avgBase.toFixed(2)}/hr → ~${(stats.avgBase * stats.wrapRate).toFixed(2)}/hr fully loaded across your catalog.
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-bw-border">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? 'border-bw-primary text-bw-primary' : 'border-transparent text-bw-muted hover:text-bw-ink'
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Active tab — embedded Costing-Studio page (canonical CRUD) */}
      <div className="bg-bw-surface rounded-xl border border-bw-border p-4 sm:p-6">
        {tab === 'labor' && <LaborCategoriesPage embedded />}
        {tab === 'indirect' && <IndirectRatesPage embedded />}
        {tab === 'odc' && <ODCCategoriesPage embedded />}
        {tab === 'wrap' && <WrapRateCalculator embedded />}
      </div>
    </div>
  );
}

export default CostingProfile;
